import { Injectable, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";

import { CerGridComponent } from "src/cer/cer-grid/cer-grid.component";
import { CerPromptService, PromptAction, PromptButton, PromptInput, PromptResult } from "@ui/cer-ui-prompt.service";

import { CerDataImportRow, CerExcelSheet, CerExcelWorkbook } from "./cer-data-import-excel-reader";
import { CerDataImportFile } from "./cer-data-import-types";
import { CerDataImportMap, CerDataImportMapActiveColumn, CerDataImportMapDest, CerDataImportMapSourceColumn, CerDataImportMapper, linkTypeIcon } from "./cer-data-import-mapper";
import { CerDataImportExcelPreMapper as CerDataImportMapSourceExcel } from "./cer-data-import-excel-pre-mapper";

@Injectable()
export class CerDataImportDialogService implements OnDestroy {
  
  constructor(
    public ui: CerPromptService) {
  }

  private closedDialog$: Subject<boolean>;
  public ngOnDestroy(): void {
    this.closedDialog$?.unsubscribe();
  }

  public async dialogOpen(dropEle: HTMLDivElement, title: string): Promise<PromptAction> {
    /*const pasteBtn = {
      text: 'Indsæt fra udklipsholder',
      iconCss: CerUiIcons.paste,
      click: (e) => { 
        document.execCommand('paste') 
      },
      action: 'paste'
    } as DialogButton;*/
    return await this.ui.info(dropEle, title, { dialogArgs: { minHeight: '90%', width: '90%', position: { X: 'center', Y: 'center' } } });
  }

  public dialogHide() {
    this.ui.promptHide();
  }

  public toast(msg: string) {
    this.ui.toast(msg);
  }

  public async importFile(files: CerExcelWorkbook[], appGrid: CerGridComponent) {
    if (files.length > 0) {
      this.dialogHide();
    }
    const sheet = await this.selectExcelSheet(files);
    if (sheet == null || sheet.rows.length == 0 || sheet.workbook.status != 'ok') {
      this.ui.errorToast("Ingen data er tilgængelig");
      return;
    }

    const mapper = await this.mapperGet(sheet, appGrid);
    if (mapper == null) {
      this.ui.toast("Import er annulleret");
      return;
    }
    if (mapper?.map.status == 'error') {
      return;
    }
    const count = await this.importRows(sheet.rows.concat(), mapper, appGrid);
  }

  private async importRows(dataRows: CerDataImportRow[], mapper: CerDataImportMapper, appGrid: CerGridComponent): Promise<number> {
    this.ui.operationBegin(`Import af ${dataRows.length} rækker starter`,"Excel Import");
    const map = mapper.map;
    const activeMap = mapper.activeMapGet(appGrid);
    var rows = dataRows.concat([]);
    const chunkSize = 50;
    var count = 0;
    if (rows.length > 0) {
      for (let i = 0; i < rows.length; i += chunkSize) {
        const batch = rows.slice(i, i + chunkSize);
        count += await this.importRowBatch(batch, activeMap, appGrid, map);
        this.ui.toast(`Import af ${count} ud af ${rows.length} rækker gennemført .. fortsætter`,"Excel Import");
      }
    }
    this.ui.operationEnd(( count > 0) ? `Import af ${count} rækker gennemført` :"Ingen rækker er importeret");
    if (count > 0)
    {
      appGrid.research();
    }
    return count;
  }

  private async importRowBatch(batchRows: CerDataImportRow[], cols: CerDataImportMapActiveColumn[], appGrid: CerGridComponent, map: CerDataImportMap): Promise<number> {
    const destRows: any[] = [];
    batchRows.forEach(sourceRow => {
      const destRow: any = {};
      cols.forEach(col => {
        if (col.hasSourceValue) {
          destRow[col.destName] = col.sourceValue;
        }
        else {
          const sourceValue: any = sourceRow.rowData[col.sourceName];
          const value: any = col.formatFn ? col.formatFn(sourceValue) : sourceValue;
          if (col.destFKData) {
            const fkValue: any = col.fkValueGet(value);
            destRow[col.destName] = fkValue;
          }
          else {
            destRow[col.destName] = value;
          }
        }
      });
      destRows.push(destRow);
    });
    return await this.saveBatch(destRows, appGrid, map)
  }

  private async saveBatch(destRows: any[], appGrid: CerGridComponent, map: CerDataImportMap): Promise<number> {
    const changes = {
      addedRecords: [] as any[],
      changedRecords: destRows,
      deletedRecords: [] as any[]
    };
    const data = appGrid.grid.renderModule.data;
    try {

      const res = await data.saveChanges(changes, map.dest.pkName, null);
      const count = (res as any[])?.length ?? 0;
      return count;
    }
    catch (error) {
      this.ui.errorApi(error);
    }
    return 0;
  }

  private async selectExcelSheet(files: CerExcelWorkbook[]): Promise<CerExcelSheet> {
    if (files == null || files?.length == 0) {
      this.ui.errorToast("Ingen filer kan læses på fra udklipsholder");
      return null;
    }

    this.ui.promptHide(); // Close paste dialog

    var workbook: CerExcelWorkbook = null;
    var sheet: CerExcelSheet = null;

    sheet = null;
    workbook = await this.pickFile(files) as CerExcelWorkbook;
    if (workbook == null) {
      this.ui.errorToast("Excel fil skal angives");
      return null;
    }
    if (workbook.status != 'ok') {
      this.ui.errorToast(`Excel fil kan ikke læses. Fejl: '${workbook.statusText}'`);
      return null;
    }
    else {
      sheet = await this.pickExcelSheet(workbook);
      if (sheet == null) {
        this.ui.errorToast("Ark i Excel fil skal vælges");
        return null;
      }
      else {
        if (sheet.rows.length <= 1) {
          this.ui.errorToast("Excel ark indeholder ikke nogen poster");
          return null;
        }
      }
    }
    return sheet;
  }

  private async pickExcelSheet(file: CerExcelWorkbook): Promise<CerExcelSheet> {
    return file.sheets.length == 1 ? file.sheets[0] : await this.pickExcelSheetDialog(file);
  }

  private async pickExcelSheetDialog(worbook: CerExcelWorkbook): Promise<CerExcelSheet> {
    return new Promise<CerExcelSheet>((resolve) => {
      const inputs: PromptInput[] = [
        { label: "Ark", id: "name", placeholder: "Vælg ark til import", value: worbook.sheets[0].name, type: 'DropDownList', dataSource: worbook.sheets, fields: { text: 'name', value: 'name' }, element: null },
        { label: "Fil", id: "fileName", placeholder: "Fil", value: worbook.name, disabled: true, element: null }
      ];
      this.ui.promptInputs('', "Vælg ark", inputs).then(
        action => {
          if (action.action == 'ok') {
            const sheetName = action.inputs[0].ej2.value;
            const sheet = worbook.sheets.find(s => s.name == sheetName);
            resolve(sheet);
          }
          else {
            resolve(null);
          }
        }
      );
    });
  }

  private async pickFile(files: CerDataImportFile[]): Promise<CerDataImportFile> {
    return files.length == 1 ? files[0] : await this.pickFileDialog(files);
  }

  private async pickFileDialog(files: CerDataImportFile[]): Promise<CerDataImportFile> {
    return new Promise<CerDataImportFile>((resolve, reject) => {
      var inputs: PromptInput[] = [
        { label: "Fil", id: "name", placeholder: "Vælg fil til import", value: files[0].name, type: 'DropDownList', dataSource: files, fields: { text: 'name', value: 'name' }, element: null }
      ];
      this.ui.promptInputsDdl('', "Vælg fil", inputs).then(
        result => {
          if (result.action == 'ok') {
            const fileName = result.inputs[0].value;
            const file = files.find(f => f.name == fileName);
            resolve(file);
          }
          else {
            reject();
          }
        }
      );
    });
  }

  private async mapperGet(sheet: CerExcelSheet, appGrid: CerGridComponent): Promise<CerDataImportMapper> {
    const mapper = this.mapExcelSheet(sheet, appGrid);
    do {
      var retry = false;
      mapper.update();
      var result = await this.confirmExcelSheet(sheet, mapper.map);
      switch (result.action) {
        case 'ok':
          if (mapper.map.status != 'error') {
            return mapper;
          }
          this.ui.errorToast("Ret venligst fejl, før import gennemføres");
          retry = true;
          break;
        case 'cancel':
          return null;
        case 'edit': /* "Edit mappings " */
          const ok = await this.mapEdit(sheet, mapper);
          retry = true;
          break;
      }
    } while (retry);

    return mapper;
  }

  private async confirmExcelSheet(sheet: CerExcelSheet, map: CerDataImportMap): Promise<PromptResult> {
    var inputs: PromptInput[] = [
      { label: "Navn fil", id: "fileName", placeholder: "Ikke tilgængelig", value: sheet.workbook.name, element: null },
      { label: "Ændret", id: "lastModified", placeholder: "Ikke tilgængelig", value: sheet.workbook.lastModified.toLocaleString(), element: null },
      { label: "Navn ark", id: "name", placeholder: "Ingen ark valgt", value: sheet.name, element: null },
      { label: "Rækker", id: "rowCount", placeholder: "Ingen rækker læst", value: sheet.rows.length.toString(), element: null }
    ];
    const reloadBtn = {
      text: 'Forbind kolonner',
      action: 'edit'
    } as PromptButton;

    inputs.forEach(i => i.disabled = true);
    const body = '<div><strong>Status:</strong></div><label class="dialog-field">' + map.statusHtml + '</label><strong><div>Ark:</strong></div>';
    return await this.ui.promptInputs(body, "Bekræft import af ark", inputs, [reloadBtn]);
  }

  private mapExcelSheet(sheet: CerExcelSheet, appGrid: CerGridComponent) {
    const viewMeta = appGrid.viewMetadata;
    const source = CerDataImportMapSourceExcel.mapExcelColumns(sheet.columns);
    const dest = new CerDataImportMapDest(viewMeta, appGrid.editService.canEdit(), appGrid.editService.canAdd());
    const map = new CerDataImportMap(source, dest);
    return new CerDataImportMapper(map);
  }

  private async mapEdit(sheet: CerExcelSheet, mapper: CerDataImportMapper): Promise<PromptAction> {
    do {
      var retry = false;
      const action = await this.mapEditDialog(sheet, mapper);
      if (action == 'reload' || action == 'retry') {
        retry = true;
      }
      else {
        return action;
      }
    }
    while (retry);

    return 'cancel';
  }

  private mapEditDialog(sheet: CerExcelSheet, mapper: CerDataImportMapper): Promise<PromptAction> {
    return new Promise<PromptAction>((resolve) => {
      const map = mapper.map;
      const title = `Forbind kolonner fra "${sheet.name}" til "${map.dest.view.text}"`;

      // Body
      var body = '<div><strong>Status:</strong></div>' +
        '<label class="dialog-field">' + map.statusHtml + '</label>' +
        '<div><strong>Ark:</strong></div>';
      body += '<div>Vælg forbindelser til kolonner:</div>';
      // Mapping
      const inputs: PromptInput[] = [];
      const ds = map.dest.columns;
      map.source.forEach(s => {
        const label = linkTypeIcon(s.linkType) + " " + (s.index?.length > 0 ? s.index + ": " : "") + (s.text ?? "") + " → ";
        const value: string = s.dest?.name;
        var input = {
          label: label,
          id: s.origin + "$" + s.index,
          sourceObject: s,
          placeholder: "Vælg destinations felt",
          value: value,
          type: 'DropDownList',
          dataSource: ds,
          fields: { text: 'text', value: 'name' },
          element: null,
        } as PromptInput;
        inputs.push(input);
      });

      // Prompt
      const btnReload = { text: 'Opdater', action: 'reload' } as PromptButton;
      const btnReset = { text: 'Nulstil', action: 'reset' } as PromptButton;
      this.ui.promptInputsDdl(body, title, inputs, [btnReload, btnReset]).then(
        result => {
          if (result.action == 'retry') {
            mapper.reset();
            mapper.update();
          }
          else if (result.action != 'cancel') {
            this.mapEditDialogSave(mapper, result.inputs);
            mapper.update();
          }
          resolve(result.action);
        }
      );
    });
  }

  private mapEditDialogSave(mapper: CerDataImportMapper, inputs: PromptInput[]) {
    const map = mapper.map;
    inputs.filter(i => i.sourceObject != null).forEach(i => {
      const s = i.sourceObject as CerDataImportMapSourceColumn;
      if (s != null) {
        const destName = i.value;
        if (s.dest != null && !(destName?.length > 0)) {
          s.dest = null;
          s.linkType = 'byUserSkip';
        }
        else {
          if (destName?.length > 0 && (s.dest == null || s.dest.name != destName)) {
            s.dest = map.dest.columns.find(d => d.name == destName);
            if (s.dest) {
              s.linkType = 'byUser';
            }

          }
        }
      }
    });
  }
}
